import {
  IAPICustomerFacingCheckOutModel,
  IAPIPurchaselinkModel,
  ICheckOutCombinedColors,
} from "../interfaces";
import { CheckOutRecommendedItemDetails } from "./CheckOutSellPageModel";
import { CheckoutPaymentPageModel } from "./CheckoutPaymentPageModel";
import { CheckOutAfterPayModel } from "./CheckOutAfterPayModel";
import {
  CheckoutOptionsPageModel,
  DonationCheckoutModel,
} from "./CheckoutOptionsPageModel";
import { SellerModel } from "./SellerModels";
import { ItemDetailInputModel } from "./ItemDetailInputModel";
import { MediaModel } from "./MediaModel";
import {
  DonationIncludedSectionModel,
  DonationPricingPageInputsModel,
  RelatedItemsCategoriesModel,
} from "./DonationAndItemModel";
import {
  MerchantAccountWithPaySettingsModel,
  PurchaseMerchantAccountSettingsModel,
} from "./MerchantApplicationInputModel";
import { CustomerFacingCheckOutItemModel } from "./PriceUtility";
import {
  CheckoutColorType,
  CheckOutDisplayTypes,
  CheckOutPayButtonCaption,
} from "../enums";
import { UtilityMethods } from "@mindmemobile/mm-shared-global";
import { CustomOptionModel } from "./CustomOptionModel";
import { PaymentModuleUtility } from "../utils/PaymentModuleUtility";

/**
 * A policy can only be presented - or required - when the seller actually has the copy for it.
 * Sellers are edited independently of the checkout or link, so terms saved as "Display Only" or
 * "Require Acceptance" can be emptied in Payment Settings at any time afterwards. Resolving that
 * to "Do Not Display" as the customer-facing model is built means the payment screen never shows
 * a checkbox with nothing behind it, and validate() never demands acceptance of a policy that no
 * longer exists - both already key off "Do Not Display", so nothing downstream has to change.
 */
export function dropPoliciesTheSellerCannotShow(
  mappedData: Partial<CustomerFacingCheckOutModel>,
): Partial<CustomerFacingCheckOutModel> {
  const { seller, paymentDetails } = mappedData;
  if (!paymentDetails) return mappedData;

  const sellerTermsAndConditions = seller?.hasTermsAndConditions
    ? paymentDetails.sellerTermsAndConditions
    : "DoNotDisplay";
  const sellerReturnPolicy = seller?.hasReturnPolicy
    ? paymentDetails.sellerReturnPolicy
    : "DoNotDisplay";

  if (
    sellerTermsAndConditions === paymentDetails.sellerTermsAndConditions &&
    sellerReturnPolicy === paymentDetails.sellerReturnPolicy
  ) {
    return mappedData;
  }

  return {
    ...mappedData,
    paymentDetails: new CheckoutPaymentPageModel({
      ...paymentDetails,
      sellerTermsAndConditions,
      sellerReturnPolicy,
    }),
  };
}

export class CustomerFacingCheckOutModel {
  name: string;
  isDeleted: boolean;
  isActive: boolean;
  isDonationOnly: boolean;
  items: CustomerFacingCheckOutItemModel[];
  recommendedItem?: CheckOutRecommendedItemDetails;
  paymentDetails: CheckoutPaymentPageModel;
  afterPayDetails: CheckOutAfterPayModel | null;
  options: CheckoutOptionsPageModel;
  showSingleItemPrice: boolean;
  displayType: keyof typeof CheckOutDisplayTypes;
  allowIncludedItems: boolean;
  allowRequiredItems: boolean;
  allowRecommendedItems: boolean;
  groupByBillingFrequency: boolean;
  merchantAccount: MerchantAccountWithPaySettingsModel;
  createdDate: Date;
  seller: SellerModel;
  donationDetailPage: ItemDetailInputModel;
  donationPricingPageData: DonationPricingPageInputsModel;
  donationCheckout: DonationCheckoutModel;
  totalCheckouts: number;
  previousRecordId: string;
  nextRecordId: string;
  index: number;
  accountId: string;
  customOption?: CustomOptionModel;
  purchaseBaseURL: string;
  constructor(data?: Partial<CustomerFacingCheckOutModel>) {
    this.accountId = data?.accountId || "";
    this.name = data?.name || "";
    this.isDeleted = data?.isDeleted || false;
    this.isActive = data?.isActive || false;
    this.isDonationOnly = data?.isDonationOnly || false;
    this.items =
      data?.items?.map((i) => new CustomerFacingCheckOutItemModel(i)) || [];
    this.recommendedItem =
      data?.recommendedItem &&
      new CheckOutRecommendedItemDetails(data?.recommendedItem);
    this.paymentDetails = data?.paymentDetails
      ? new CheckoutPaymentPageModel(data?.paymentDetails)
      : new CheckoutPaymentPageModel();
    this.afterPayDetails = data?.afterPayDetails
      ? new CheckOutAfterPayModel(data?.afterPayDetails)
      : null;
    this.options = new CheckoutOptionsPageModel(data?.options);
    this.showSingleItemPrice = data?.showSingleItemPrice || false;
    this.displayType = data?.displayType || CheckOutDisplayTypes.SingleItem;
    this.allowIncludedItems = data?.allowIncludedItems || false;
    this.allowRequiredItems = data?.allowRequiredItems || false;
    this.allowRecommendedItems = data?.allowRecommendedItems || false;
    this.groupByBillingFrequency = data?.groupByBillingFrequency || false;
    this.merchantAccount = new MerchantAccountWithPaySettingsModel(
      data?.merchantAccount,
    );
    this.createdDate = data?.createdDate || new Date();
    this.seller = data?.seller
      ? new SellerModel(data?.seller)
      : new SellerModel();
    this.donationDetailPage = data?.donationDetailPage
      ? new ItemDetailInputModel(data?.donationDetailPage)
      : new ItemDetailInputModel();
    this.donationPricingPageData = data?.donationPricingPageData
      ? new DonationPricingPageInputsModel(data?.donationPricingPageData)
      : new DonationPricingPageInputsModel();
    this.donationCheckout =
      data?.donationCheckout || new DonationCheckoutModel();
    this.totalCheckouts = data?.totalCheckouts || 0;
    this.previousRecordId = data?.previousRecordId || "";
    this.nextRecordId = data?.nextRecordId || "";
    this.index = data?.index || 0;
    this.customOption =
      data?.customOption && new CustomOptionModel(data?.customOption);
    this.purchaseBaseURL = PaymentModuleUtility.removeLastBackslash(
      data?.purchaseBaseURL,
    );
  }

  static deserializeAPIModel(data: IAPICustomerFacingCheckOutModel) {
    const mappedData: Partial<CustomerFacingCheckOutModel> = {
      purchaseBaseURL: data.purchaseBaseURL,
      accountId: data.accountId,
      isDeleted: data?.isDeleted,
      isActive: data?.isActive,
      isDonationOnly: data?.isDonationOnly,
      items: data?.items?.map((i) =>
        CustomerFacingCheckOutItemModel.deserializeAPIModel(
          i,
          data?.isDonationOnly,
          false,
          data.donation,
        ),
      ),
      recommendedItem:
        data?.recommendedItem &&
        CheckOutRecommendedItemDetails.deserializeAPIModel(
          data?.recommendedItem,
        ),
      paymentDetails:
        data?.payDetails &&
        CheckoutPaymentPageModel.deserializeAPIModel(
          data?.payDetails,
          data?.applyPromoCode,
        ),
      afterPayDetails: data?.afterPayDetails
        ? CheckOutAfterPayModel.deserializeAPIModel(data?.afterPayDetails)
        : null,
      options: CheckoutOptionsPageModel.deserializeAPIModel(data?.options),
      showSingleItemPrice: data?.showSingleItemPrice,
      displayType: data?.displayType || CheckOutDisplayTypes.SingleItem,
      allowIncludedItems: data?.allowIncludedItems,
      allowRequiredItems: data?.allowRequiredItems,
      allowRecommendedItems: data?.allowRecommendedItems,
      groupByBillingFrequency: data?.groupByBillingFrequency,
      merchantAccount:
        data.merchantAccount &&
        MerchantAccountWithPaySettingsModel.deserializeAPIModel(
          data.merchantAccount,
        ),
      name: data.name,
      createdDate: UtilityMethods.removeZFromDateString(data.createdDate),
      seller: SellerModel.deserializeAPIModel(data?.seller),
      donationDetailPage: data.donation
        ? ItemDetailInputModel.deserializeAPIModel(data.donation)
        : new ItemDetailInputModel(),
      donationPricingPageData: data.donation?.price
        ? DonationPricingPageInputsModel.deserializeAPIModelForCustomerFacing(
            data.donation.price,
          )
        : new DonationPricingPageInputsModel(),
      donationCheckout: DonationCheckoutModel.deserializeAPIModel(
        data?.mediaPlacement,
        data.media
          ? MediaModel.deserializeAPIModel(data.media)
          : new MediaModel(),
        data?.options?.customColors,
      ),
      totalCheckouts: data.total,
      previousRecordId: data.previousRecordId,
      nextRecordId: data.nextRecordId,
      index: data.index,
      customOption:
        data.customOption &&
        CustomOptionModel.deserializeAPIModel(data.customOption),
    };
    return new CustomerFacingCheckOutModel(
      dropPoliciesTheSellerCannotShow(mappedData),
    );
  }

  static deserializePurchaseLinkAPIModel(
    data: IAPIPurchaselinkModel,
  ): CustomerFacingCheckOutModel {
    const mappedData: Partial<CustomerFacingCheckOutModel> = {
      accountId: data.accountId,
      name: data.name,
      isDeleted: false,
      isActive: data.isActive,
      isDonationOnly: data.itemType === "Donation",
      items:
        data.itemType === "Donation"
          ? [
              new CustomerFacingCheckOutItemModel({
                itemId: data.donation.id,
                itemType: data.itemType,
                name: data.donation.name,
                isActive: data.donation.isActive,
                id: data.id,
                description: data.donation.description,
                quantity: 1,
                prices: [],
                medias:
                  data.donation.medias?.map((m) =>
                    MediaModel.deserializeAPIModel(m),
                  ) || [],
                // recommendedItems: data.donation.options?.recommended
                //   ? data.donation.options?.recommended?.items.map((i) =>
                //       RelatedItemsCategoriesModel.deserializeAPIModel(i)
                //     )
                //   : [],
                // includedItems:
                //   data.donation.options?.included &&
                //   data.donation.options?.included?.item
                //     ? data.donation.options?.included?.item?.items.map((i) =>
                //         RelatedItemsCategoriesModel.deserializeAPIModel(i)
                //       )
                //     : [],
                requiredItems:
                  data.itemDetails?.requiredItems?.map((i) =>
                    RelatedItemsCategoriesModel.deserializeAPIModel(i),
                  ) || [],
                recommendedItems:
                  data.itemDetails?.recommendedItems?.map((i) =>
                    RelatedItemsCategoriesModel.deserializeAPIModel(i),
                  ) || [],
                //INCLUDED ITEMS WILL BE BASED ON DONATION RANGE OPTIONS
                includedItems:
                  data.itemDetails?.includedItems?.map((i) =>
                    RelatedItemsCategoriesModel.deserializeAPIModel(i),
                  ) || [],
                donationIncludedDetails: data.donation.options?.included
                  ? DonationIncludedSectionModel.deserializeAPIModel(
                      data.donation.options.included,
                    )
                  : undefined,
                donationIncludedItemsByRangeCategories:
                  data.itemDetails?.donationRangeIncludedItems?.map((i) =>
                    RelatedItemsCategoriesModel.deserializeAPIModel(i),
                  ) || [],
                descriptionEditorRawContent:
                  data.donation.descriptionEditorRawContent || "",
                hasVariants: data.donation.hasVariants,
                hasSurcharge: data.donation.surcharge,
                tax: data.donation.tax,
                purchaseLinkSurcharge: data.options.surcharge,
              }),
            ]
          : data.itemDetails
            ? data.priceOption === "AllActive"
              ? data.itemDetails.prices.map((price) =>
                  CustomerFacingCheckOutItemModel.deserializeAPIModel(
                    {
                      ...data.itemDetails,
                      itemId: data.itemId,
                      itemType: data.itemType,
                      includedItems: data.itemDetails.includedItems || [],
                      recommendedItems: data.itemDetails.recommendedItems || [],
                      requiredItems: data.itemDetails.requiredItems || [],
                      price: price,
                      surcharge: data.itemDetails.surcharge,
                      tax: data.options.tax,
                    },
                    false,
                    data.options.surcharge,
                    undefined,
                    data.variantIds?.length > 0 ? data.variantIds : undefined,
                  ),
                )
              : data.itemDetails.prices
                  .filter((price) => data.priceIds.includes(price.id))
                  .map((price) =>
                    CustomerFacingCheckOutItemModel.deserializeAPIModel(
                      {
                        ...data.itemDetails,
                        itemId: data.itemId,
                        itemType: data.itemType,
                        includedItems: data.itemDetails.includedItems || [],
                        recommendedItems:
                          data.itemDetails.recommendedItems || [],
                        requiredItems: data.itemDetails.requiredItems || [],
                        price: price,
                        surcharge: data.itemDetails.surcharge,
                        tax: data.options.tax,
                      },
                      false,
                      data.options.surcharge,
                      undefined,
                      data.variantIds?.length > 0 ? data.variantIds : undefined,
                    ),
                  )
            : [],
      merchantAccount: data.merchantAccount
        ? MerchantAccountWithPaySettingsModel.deserializeAPIModel(
            data.merchantAccount,
          )
        : new MerchantAccountWithPaySettingsModel(),
      recommendedItem:
        (data.itemDetails &&
          data.itemDetails.recommendedItems?.length > 0 &&
          CheckOutRecommendedItemDetails.deserializeAPIModel({
            itemId: data.itemDetails.recommendedItems[0].id,
            caption: "",
            priceId: data.itemDetails.recommendedItems[0].prices[0].id,
          })) ||
        undefined,
      /* Built for every link, donation ones included: the seller policy and
         surcharge settings live on the link itself, not on itemDetails, and
         gating this on itemDetails left donation links with a default payment
         model - which is what stopped the seller's Terms & Conditions and
         Return Policy from ever appearing on the purchase screen. */
      paymentDetails: new CheckoutPaymentPageModel({
        surchargeEnabled: data.surchargeEnabled ?? false,
        surchargePercentage:
          data.surchargePercentage != null
            ? data.surchargePercentage.toString()
            : "",
        surchargeAskCustomerFirst: data.surchargeAskCustomerFirst ?? false,
        /* Same fallback as PurchaseLinkModel: a link saved before these were
           configurable has not opted into showing either policy. */
        sellerTermsAndConditions:
          data.sellerTermsAndConditionsType || "DoNotDisplay",
        sellerReturnPolicy: data.sellerReturnPolicyType || "DoNotDisplay",
      }),
      afterPayDetails: new CheckOutAfterPayModel(),
      options: new CheckoutOptionsPageModel(),
      showSingleItemPrice:
        data.itemDetails && data.itemDetails.prices?.length === 1
          ? true
          : false,
      displayType:
        data.itemType === "Donation"
          ? CheckOutDisplayTypes.SingleItem
          : data.priceIds?.length === 1 ||
              (data.priceOption === "AllActive" &&
                data.itemDetails?.prices?.length === 1)
            ? CheckOutDisplayTypes.SingleItem
            : CheckOutDisplayTypes.SingleItemWithMultiplePrices,
      allowIncludedItems: true,
      allowRequiredItems: true,
      allowRecommendedItems: true,
      groupByBillingFrequency: false,
      seller: SellerModel.deserializeAPIModel(data.seller),
      donationDetailPage:
        data.itemType === "Donation"
          ? ItemDetailInputModel.deserializeAPIModel(data.donation)
          : new ItemDetailInputModel(),
      donationPricingPageData:
        data.itemType === "Donation"
          ? DonationPricingPageInputsModel.deserializeAPIModelForCustomerFacing(
              data.donation.price,
            )
          : new DonationPricingPageInputsModel(),
    };

    return new CustomerFacingCheckOutModel(
      dropPoliciesTheSellerCannotShow(mappedData),
    );
  }

  getSortedItems(): CustomerFacingCheckOutItemModel[] {
    const sortedItems = [...this.items].sort(
      (a, b) => a.priceInNumber - b.priceInNumber,
    );
    if (this.recommendedItem) {
      const itemIndex = sortedItems.findIndex(
        (item) =>
          item.itemId === this.recommendedItem?.itemId &&
          item.price.id === this.recommendedItem?.priceId,
      );
      if (itemIndex !== -1 && itemIndex !== 1) {
        const [matchedItem] = sortedItems.splice(itemIndex, 1);
        sortedItems.splice(1, 0, matchedItem);
      }
    }
    return sortedItems;
  }

  getColors() {
    const brandingData = this.options?.brandingData;
    if (!brandingData) return undefined;

    switch (brandingData.selectedColorType) {
      case CheckoutColorType.BasicColor:
        return brandingData.basicColors.getBasicColors() as ICheckOutCombinedColors;
      case CheckoutColorType.CustomColor:
        return brandingData.customColors.getCustomColors() as ICheckOutCombinedColors;
    }
  }

  getButtonCaption = () => {
    const brandingData = this.options?.brandingData;
    if (!brandingData) return null;
    switch (brandingData.selectedColorType) {
      case CheckoutColorType.BasicColor:
        return brandingData.basicColors.payButtonType ===
          CheckOutPayButtonCaption.Custom
          ? brandingData.basicColors.buttonCustomCaption
          : CheckOutPayButtonCaption[brandingData.basicColors.payButtonType];
      case CheckoutColorType.CustomColor:
        return brandingData.customColors.payButtonType ===
          CheckOutPayButtonCaption.Custom
          ? brandingData.customColors.buttonCustomCaption
          : CheckOutPayButtonCaption[brandingData.customColors.payButtonType];
    }
  };

  getButtonStyle = () => {
    const brandingData = this.options?.brandingData;
    if (!brandingData) return null;

    switch (brandingData.selectedColorType) {
      case CheckoutColorType.BasicColor:
        return brandingData.basicColors.buttonStyleType;
      case CheckoutColorType.CustomColor:
        return brandingData.customColors.buttonStyleType;
    }
  };
}

export class CustomerFacingCheckOutMerchantModel {
  name: string;
  imageURL: string;
  gateway: string;
  applicationId: string;
  merchantAccountId: string;
  constructor(data?: Partial<CustomerFacingCheckOutMerchantModel>) {
    this.name = data?.name || "";
    this.imageURL = data?.imageURL || "";
    this.gateway = data?.gateway || "";
    this.applicationId = data?.applicationId || "";
    this.merchantAccountId = data?.merchantAccountId || "";
  }
}
